import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { getCoverImageSrc, StyleRecipeSeed } from '@/data/style-recipes'
import { GripVertical } from 'lucide-react'
import { useState } from 'react'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from './ui/breadcrumb'
import { motion } from 'motion/react'
import { cn } from '@/lib/utils'

export function StyleRecipeModal({
  recipe,
  children,
}: {
  recipe: StyleRecipeSeed
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(false)
  const coverImageSrc = getCoverImageSrc(recipe.coverImage)

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="bg-foreground/5 ring-foreground/5 max-w-md gap-0 rounded-2xl border-none p-[2px] shadow-lg ring-1 backdrop-blur-xl">
        <DialogHeader className="p-2">
          <div className="flex w-full flex-col">
            <DialogTitle className="flex items-center text-sm font-medium">
              <div className="ml-2 flex items-center justify-center">
                <GripVertical className="text-foreground/60 size-4" />
              </div>
              &nbsp;
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem className="hidden md:block">
                    {/* <BreadcrumbLink href="#">Style Recipes</BreadcrumbLink> */}
                    <BreadcrumbPage>Style Recipes</BreadcrumbPage>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className="hidden md:block" />
                  <BreadcrumbItem>
                    <BreadcrumbPage>{recipe.name}</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </DialogTitle>
            <DialogDescription className="text-foreground/60 sr-only p-2 text-xs">
              Style recipe details and configuration.
            </DialogDescription>
          </div>
        </DialogHeader>
        <div className="bg-background dark:bg-background/85 ring-foreground/15 flex max-h-[700px] w-full max-w-full flex-1 flex-col gap-6 overflow-y-auto rounded-2xl p-0 ring-1">
          <div className="relative w-full border">
            {coverImageSrc && (
              <div className="ring-foreground/5 bg-foreground/5 overflowhidden aspect-square h-full w-full rounded-2xl shadow ring-1">
                <img
                  src={coverImageSrc}
                  alt={recipe.name}
                  className="h-full w-full rounded-2xl object-cover"
                />
              </div>
            )}
            <motion.div
              className={cn(
                'pointer-events-none absolute inset-0 z-1 flex flex-col justify-end overflow-hidden rounded-2xl',
                "before:absolute before:inset-0 before:mask-[linear-gradient(to_bottom,#0000_50%,black_85%)] before:backdrop-blur-lg before:content-['']",
              )}
            >
              <div className="relative z-10 flex flex-col gap-2 p-8">
                <h4 className="text-foreground font-semibold">{recipe.name}</h4>
                <p className="text-muted-foreground">{recipe.description}</p>
              </div>
            </motion.div>
          </div>
          <div className="flex flex-col gap-4 p-8 pt-0">
            <div>
              <strong className="text-foreground">Result:</strong>
              <p className="text-muted-foreground mt-1">{recipe.quality}</p>
            </div>
            <div>
              <strong className="text-foreground">Characteristics:</strong>
              <p className="text-muted-foreground mt-1">
                {recipe.adds.join(', ')}.
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
